
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AccountWithdrawService } from '../../services/account-withdraw-service';
import { AccountsControllerService } from '../../services/generated/services';
import { SharedModule } from '../../shared/shared.module';

type TxnApprovalStep = 'loading' | 'details' | 'otp' | 'result' | 'error';

@Component({
  selector: 'app-txn-approval',
  standalone: true,
  imports: [FormsModule, SharedModule],
  templateUrl: './txn-approval.component.html',
  styleUrl: './txn-approval.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TxnApprovalComponent implements OnInit, OnDestroy {
  token: string = '';
  step: TxnApprovalStep = 'loading';

  // From get_transfer_type_by_transfer_id — decides which OTP-verify endpoint to call.
  transferType: 'DEPOSIT' | 'WITHDRAWAL' | '' = '';
  firmId: any = null;

  // From get_cash_transfer_details_by_transfer_id — same SenderDetails/RecipientDetails/
  // TransactionDetails shape this page already rendered from the old initiate response.
  withdrawCashMomentData: any = null;
  private transferId: any = null;

  loadError: string = '';

  // OTP step
  otp: string = '';
  otpError: string = '';
  isResendingOtp = false;
  isSubmittingOtp = false;
  isCancelling = false;
  private webOtpAbortController: AbortController | null = null;

  // Final outcome (reused by the existing banner helpers below).
  message: string = '';
  status: string = '';
  v_tran_status: string = '';

  constructor(private route: ActivatedRoute,
    private accountWithdrawService: AccountWithdrawService,
    private accountsControllerService: AccountsControllerService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.token = params.get('token') || '';
      this.loadTransferForApproval();
    });
  }

  ngOnDestroy(): void {
    this.stopWebOtpAutofill();
  }

  /** WebOTP (Android Chrome only) — genuinely zero-tap: the browser watches incoming SMS itself
   *  and hands us the code the moment a matching one arrives, no user interaction needed. Requires
   *  the SMS text to end with "@<domain> #<code>" for the browser to trust it belongs to this site;
   *  without that backend-side suffix this silently resolves nothing and the user just falls back
   *  to typing/pasting or the plain autocomplete="one-time-code" suggestion. Safe to call whether or
   *  not the browser/backend supports it — unsupported browsers skip straight past the `if`, and an
   *  unmatched SMS just leaves the promise pending until aborted.
   */
  private startWebOtpAutofill(): void {
    this.stopWebOtpAutofill();
    if (!('OTPCredential' in window) || !navigator.credentials) {
      return;
    }
    const controller = new AbortController();
    this.webOtpAbortController = controller;
    (navigator.credentials as any)
      .get({ otp: { transport: ['sms'] }, signal: controller.signal })
      .then((credential: any) => {
        const code = (credential?.code ?? '').replace(/\D/g, '').slice(0, 6);
        if (code) {
          this.otp = code;
          this.otpError = '';
          this.changeDetectorRef.detectChanges();
        }
      })
      .catch(() => { /* aborted, unsupported, or no matching SMS arrived — ignore */ });
  }

  private stopWebOtpAutofill(): void {
    this.webOtpAbortController?.abort();
    this.webOtpAbortController = null;
  }

  private loadTransferForApproval(): void {
    if (!this.token) {
      this.step = 'error';
      this.loadError = 'This link is missing its transaction reference.';
      this.changeDetectorRef.detectChanges();
      return;
    }
    this.step = 'loading';
    this.accountWithdrawService.get_transfer_type_by_transfer_id(this.token).subscribe({
      next: (data: any) => {
        this.transferType = data?.TransferType === 'WITHDRAWAL' ? 'WITHDRAWAL' : (data?.TransferType === 'DEPOSIT' ? 'DEPOSIT' : '');
        this.firmId = data?.FirmID ?? null;
        if (!this.transferType) {
          this.step = 'error';
          this.loadError = 'This transaction link is no longer valid.';
          this.changeDetectorRef.detectChanges();
          return;
        }
        this.fetchTransferDetails();
      },
      error: () => {
        this.step = 'error';
        this.loadError = 'This transaction link is no longer valid.';
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  private fetchTransferDetails(): void {
    this.accountWithdrawService.get_cash_transfer_details_by_transfer_id(this.token).subscribe({
      next: (data: any) => {
        const details = this.normalizeTransferDetailsResponse(data);
        this.withdrawCashMomentData = details;
        this.transferId = this.extractTransferId(details);
        if (!details || !this.transferId) {
          this.step = 'error';
          this.loadError = 'We could not find the details for this transaction.';
        } else {
          this.step = 'details';
        }
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        this.step = 'error';
        this.loadError = 'We could not find the details for this transaction.';
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  /** Response is a flat object — {TransferID, TransferType, SenderName, SenderAccount,
   *  RecipientName, RecipientAccount, BankName, RoutingNumber, TransferAmount, ...} — just
   *  defend against a string-encoded or array-wrapped body. */
  private normalizeTransferDetailsResponse(resp: any): any {
    let value = resp;
    if (typeof value === 'string') {
      try { value = JSON.parse(value); } catch { return null; }
    }
    if (Array.isArray(value)) {
      value = value[0];
    }
    return value ?? null;
  }

  private extractTransferId(details: any): any {
    return details?.TransferID ?? null;
  }

  private extractTransferAmount(): number {
    const amount = Number(this.withdrawCashMomentData?.TransferAmount);
    return Number.isFinite(amount) ? amount : 0;
  }

  /** The linked-bank side of the transfer — Sender for a DEPOSIT, Recipient for a WITHDRAWAL. */
  get externalParty(): { name: string; accountNumber: string; bankName: string; routingNumber: string } | null {
    const d = this.withdrawCashMomentData;
    if (!d) return null;
    const isDeposit = this.transferType === 'DEPOSIT';
    return {
      name: (isDeposit ? d.SenderName : d.RecipientName) ?? '--',
      accountNumber: this.maskDetails(isDeposit ? d.SenderAccount : d.RecipientAccount),
      bankName: d.BankName ?? '--',
      routingNumber: d.RoutingNumber ?? '--',
    };
  }

  /** The HYSA side of the transfer — Recipient for a DEPOSIT, Sender for a WITHDRAWAL. */
  get hysaParty(): { name: string; accountNumber: string } | null {
    const d = this.withdrawCashMomentData;
    if (!d) return null;
    const isDeposit = this.transferType === 'DEPOSIT';
    return {
      name: (isDeposit ? d.RecipientName : d.SenderName) ?? '--',
      accountNumber: this.maskDetails(isDeposit ? d.RecipientAccount : d.SenderAccount),
    };
  }

  /** "Continue" on the details screen: resend the OTP for this transfer, then reveal the OTP entry. */
  onContinueClick(): void {
    if (!this.transferId || this.isResendingOtp) {
      return;
    }
    this.isResendingOtp = true;
    this.otpError = '';
    this.accountsControllerService.resendOtp({
      body: {
        TransferID: this.transferId,
        TransferAmount: this.extractTransferAmount(),
        isAdvisor: false,
      } as any,
    }).subscribe({
      next: () => {
        this.isResendingOtp = false;
        this.otp = '';
        this.step = 'otp';
        this.startWebOtpAutofill();
        this.changeDetectorRef.detectChanges();
      },
      error: (error: any) => {
        this.isResendingOtp = false;
        this.otpError = error?.error?.v_msg || error?.error?.message || 'Unable to send a code. Please try again.';
        this.changeDetectorRef.detectChanges();
      },
    });
  }

  /** "Cancel" on the details screen — same cancel_transaction_sms call already bound to the
   *  Approval-Pending cards in the advisor/client HYSA pages. */
  onCancelClick(): void {
    if (!this.transferId || this.isCancelling) {
      return;
    }
    this.isCancelling = true;
    this.accountsControllerService.cancelTransactionSms({
      body: {
        TransferID: this.transferId,
        TransferAmount: this.extractTransferAmount(),
        isAdvisor: true,
      } as any,
    }).subscribe({
      next: (resp: any) => {
        this.isCancelling = false;
        if (resp?.v_status === 'SUCCESS') {
          this.status = 'SUCCESS';
          this.message = 'This transfer has been cancelled.';
          this.v_tran_status = 'Cancelled';
          this.step = 'result';
        } else {
          this.loadError = resp?.v_msg || 'Unable to cancel this transfer. Please try again.';
        }
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        this.isCancelling = false;
        this.loadError = 'Unable to cancel this transfer. Please try again.';
        this.changeDetectorRef.detectChanges();
      },
    });
  }

  /** Single OTP input now (was 6 split boxes) — autocomplete="one-time-code" on one field is what
   *  actually triggers the native "Code from Messages"/SMS suggestion on both iOS and Android;
   *  the old split-box layout was unreliable for that. Sanitizes whatever lands in the field
   *  (typed, pasted, or autofilled — autofill can include the code plus surrounding text/spaces
   *  on some browsers) down to digits only, capped at 6. */
  onOtpValueChange(value: string): void {
    const sanitized = (value ?? '').replace(/\D/g, '').slice(0, 6);
    if (sanitized !== value) {
      this.otp = sanitized;
    }
    this.otpError = '';
  }

  resendOtp(): void {
    this.onContinueClick();
  }

  /** OTP submit: same initiate-with-OTP endpoints the advisor/client dashboards use, picking
   *  the add-cash vs withdraw variant off the TransferType from get_transfer_type_by_transfer_id. */
  onOtpSubmit(): void {
    if (!this.transferId || (this.otp?.length ?? 0) < 6 || this.isSubmittingOtp) {
      this.otpError = 'Please enter the 6-digit code.';
      this.changeDetectorRef.detectChanges();
      return;
    }
    this.isSubmittingOtp = true;
    this.otpError = '';

    const request$ = this.transferType === 'DEPOSIT'
      ? this.accountsControllerService.initiateAddCashTransferWithOtp({
          body: {
            transferId: this.transferId,
            otp: this.otp,
            firmID: String(this.firmId ?? ''),
          } as any,
        })
      : this.accountsControllerService.initiateWithdrawCashTransferWithOtp({
          body: {
            transferId: this.transferId,
            otp: this.otp,
          } as any,
        });

    request$.subscribe({
      next: (response: any) => {
        this.isSubmittingOtp = false;
        if (response?.v_status === 'SUCCESS' || response?.status === 'SUCCESS') {
          this.stopWebOtpAutofill();
          this.status = 'SUCCESS';
          // Always the frontend's own wording here, not the backend's v_msg (which can say
          // "transaction is initiated" even though, from the user's perspective, approving via
          // OTP is the final step — the transfer is approved).
          this.message = 'Your transaction is approved.';
          this.v_tran_status = 'Approved';
          this.step = 'result';
        } else {
          this.otpError = response?.v_msg || response?.message || 'That code didn\'t work. Please try again.';
        }
        this.changeDetectorRef.detectChanges();
      },
      error: (error: any) => {
        this.isSubmittingOtp = false;
        this.otpError = error?.error?.v_msg || error?.error?.message || 'That code didn\'t work. Please try again.';
        this.changeDetectorRef.detectChanges();
      },
    });
  }

  maskDetails(item: string | null): string {
    if (!item || item.length < 4) return "--"; // Handle null or invalid item
    const lastFourDigits = item.slice(-4);
    return `XXXX${lastFourDigits}`;
  }

  isSuccessBanner(): boolean {
    if (this.status === 'SUCCESS') {
      return true;
    }

    const normalizedMessage = (this.message || '').toLowerCase();
    return (
      normalizedMessage.includes('already processed') ||
      normalizedMessage.includes('already done') ||
      normalizedMessage.includes('has been initiated') ||
      normalizedMessage.includes('approved') ||
      normalizedMessage.includes('completed')
    );
  }

  isExpired(): boolean {
    return (this.message || '').toLowerCase().includes('expired');
  }

  getBannerTitle(): string {
    if (this.isExpired()) {
      return 'This transaction link has expired';
    }

    return this.message;
  }

  getBannerSubtitle(): string {
    if (this.isExpired()) {
      return 'The reference below is read-only. Ask your advisor to generate a new link if action is still needed on this transfer.';
    }

    if (this.isSuccessBanner()) {
      const normalizedMessage = (this.message || '').toLowerCase();
      if (normalizedMessage.includes('already processed') || normalizedMessage.includes('already done')) {
        return 'The reference below is read-only. No further action is required on this transfer.';
      }

      return 'The details below are for your reference.';
    }

    return 'Please contact your advisor if you need assistance with this transfer.';
  }
}
